import { Controller } from "@hotwired/stimulus"

// FD-126: save an abandoned "new work item" form as a draft. If the modal is
// dismissed (Escape / backdrop / programmatic close) with a title typed but never
// submitted, POST it as a draft so the work isn't lost. Explicit Discard skips it;
// submitting (Create / Save as draft) skips it too.
//
// Dismiss is detected from several signals — the dialog `close` event (fires for
// every native dismiss in real browsers), an Escape `keydown` fallback, and a
// backdrop click — because not every environment emits the `close` event. A
// one-shot `saved` guard keeps overlapping signals from saving twice; it re-arms
// when the user types again.
//
// Wire on the modal <form>: data-controller="draft-autosave",
// data-draft-autosave-target="title" on the title input, and
// data-action="draft-autosave#discard" on the Discard button.
export default class extends Controller {
  static targets = ["title"]

  connect() {
    this.dialog = this.element.closest("dialog")
    this.rearm()
    this.onClose = () => this.maybeSave()
    this.onKeydown = (e) => { if (e.key === "Escape") this.maybeSave() }
    this.onBackdropClick = (e) => { if (e.target === this.dialog) this.maybeSave() }
    this.onSubmit = () => { this.submitted = true }
    this.onInput = () => { this.saved = false }

    this.dialog?.addEventListener("close", this.onClose)
    this.dialog?.addEventListener("keydown", this.onKeydown)
    this.dialog?.addEventListener("click", this.onBackdropClick)
    this.element.addEventListener("submit", this.onSubmit)
    this.element.addEventListener("input", this.onInput)
  }

  disconnect() {
    this.dialog?.removeEventListener("close", this.onClose)
    this.dialog?.removeEventListener("keydown", this.onKeydown)
    this.dialog?.removeEventListener("click", this.onBackdropClick)
    this.element.removeEventListener("submit", this.onSubmit)
    this.element.removeEventListener("input", this.onInput)
  }

  // Marked by the Discard button so dismissing it doesn't auto-save.
  discard() { this.discarded = true }

  rearm() { this.submitted = false; this.discarded = false; this.saved = false }

  maybeSave() {
    if (this.saved || this.submitted || this.discarded) return
    if (!this.hasTitleTarget || this.titleTarget.value.trim() === "") return
    this.saved = true // one-shot: don't double-save from overlapping dismiss signals

    const data = new FormData(this.element)
    data.set("save_as_draft", "1")
    const token = document.querySelector("meta[name=csrf-token]")?.content
    fetch(this.element.action, {
      method: "POST",
      headers: { Accept: "application/json", ...(token && { "X-CSRF-Token": token }) },
      body: data,
      keepalive: true
    }).catch(() => {})

    this.element.reset() // clear so reopening starts fresh (and we don't re-save the same draft)
  }
}
